<template>
  <div class="telegram-link-panel">
    <div class="telegram-link-panel__section-header">
      <v-icon size="18" class="telegram-link-panel__section-icon">mdi-send-circle-outline</v-icon>
      <span>{{ $t("settings.profile.telegram.title") }}</span>
    </div>
    <p class="text-body-2 text-medium-emphasis mb-3">
      {{ $t("settings.profile.telegram.helper") }}
    </p>

    <v-progress-circular v-if="loading" indeterminate size="20" />

    <template v-else>
      <!-- No bot available -->
      <v-alert
        v-if="!hasActiveBot"
        type="info"
        density="compact"
        class="mb-2"
      >
        {{ $t("settings.profile.telegram.noBot") }}
      </v-alert>

      <!-- Already linked -->
      <template v-else-if="linkedToBot">
        <div class="telegram-link-panel__status d-flex align-center ga-3">
          <v-icon color="success">mdi-check-circle</v-icon>
          <div>
            <div class="font-weight-medium">
              {{
                $t("settings.profile.telegram.linkedTo", {
                  bot: "@" + linkedToBot.bot_username,
                })
              }}
            </div>
            <div v-if="linkedToBot.telegram_username" class="text-caption text-medium-emphasis">
              {{ $t("settings.profile.telegram.linkedAs") }} @{{ linkedToBot.telegram_username }}
            </div>
          </div>
          <v-spacer />
          <v-btn size="small" variant="text" color="error" @click="unlink">
            {{ $t("settings.profile.telegram.unlink") }}
          </v-btn>
        </div>
      </template>

      <!-- Not linked → show generate code -->
      <template v-else>
        <!-- Bot selector when company has more than one bot -->
        <v-select
          v-if="activeBots.length > 1"
          v-model="selectedAccountId"
          :items="botOptions"
          :label="$t('settings.profile.telegram.pickBot')"
          hide-details
          class="mb-3"
        />

        <div v-if="activeCode" class="telegram-link-panel__code-card">
          <div class="text-caption text-medium-emphasis mb-1">
            {{ $t("settings.profile.telegram.codeReady") }}
          </div>
          <div class="d-flex align-center ga-3 flex-wrap">
            <code class="telegram-link-panel__code">{{ activeCode.code }}</code>
            <v-btn
              size="x-small"
              variant="tonal"
              prepend-icon="mdi-content-copy"
              @click="copyCode"
            >
              {{ $t("settings.profile.telegram.copy") }}
            </v-btn>
            <v-chip size="x-small" color="warning">
              {{ $t("settings.profile.telegram.expiresIn", { time: countdownLabel }) }}
            </v-chip>
          </div>
          <div class="text-caption text-medium-emphasis mt-2">
            {{
              $t("settings.profile.telegram.howTo", {
                bot: selectedBotUsername ? "@" + selectedBotUsername : "bot-ul",
                command: "/start " + activeCode.code,
              })
            }}
          </div>
          <div class="mt-3">
            <v-btn size="small" variant="text" @click="generate">
              {{ $t("settings.profile.telegram.regenerate") }}
            </v-btn>
          </div>
        </div>

        <v-btn
          v-else
          color="primary"
          variant="tonal"
          prepend-icon="mdi-key-variant"
          :loading="generating"
          :disabled="!selectedAccountId"
          @click="generate"
        >
          {{ $t("settings.profile.telegram.generate") }}
        </v-btn>
      </template>

      <v-alert v-if="errorMessage" type="error" density="compact" class="mt-3">
        {{ errorMessage }}
      </v-alert>
    </template>

    <v-snackbar v-model="snackbar.show" :timeout="2500" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "TelegramUserLinkPanel",
  data() {
    return {
      loading: false,
      generating: false,
      activeBots: [],
      links: [],
      activeCode: null,
      selectedAccountId: null,
      errorMessage: "",
      countdownLabel: "",
      countdownTimer: null,
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
    };
  },
  computed: {
    hasActiveBot() {
      return this.activeBots.length > 0;
    },
    linkedToBot() {
      if (!this.selectedAccountId) return null;
      return this.links.find((l) => l.account_id === this.selectedAccountId) || null;
    },
    botOptions() {
      return this.activeBots.map((b) => ({
        title: `${b.name}${b.bot_username ? ` (@${b.bot_username})` : ""}`,
        value: b.id,
      }));
    },
    selectedBotUsername() {
      const bot = this.activeBots.find((b) => b.id === this.selectedAccountId);
      return bot?.bot_username || null;
    },
  },
  async mounted() {
    await this.refresh();
  },
  beforeUnmount() {
    this.stopCountdown();
  },
  methods: {
    showSnackbar(message, color = "info") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    extractError(error, fallback) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
      );
    },
    async refresh() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const [accountsRes, linksRes] = await Promise.all([
          axios.get("/integrations/telegram/link/accounts"),
          axios.get("/integrations/telegram/link/me"),
        ]);
        this.activeBots = Array.isArray(accountsRes.data?.accounts)
          ? accountsRes.data.accounts
          : [];
        this.links = Array.isArray(linksRes.data?.links) ? linksRes.data.links : [];

        if (!this.selectedAccountId && this.activeBots.length > 0) {
          this.selectedAccountId = this.activeBots[0].id;
        }
        await this.fetchActiveCode();
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          this.$t("settings.profile.telegram.errors.load")
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchActiveCode() {
      this.activeCode = null;
      this.stopCountdown();
      if (!this.selectedAccountId || this.linkedToBot) return;
      try {
        const { data, status } = await axios.get("/integrations/telegram/link/code", {
          params: { accountId: this.selectedAccountId },
          validateStatus: (s) => s === 200 || s === 204,
        });
        if (status === 200 && data?.code) {
          this.activeCode = {
            code: data.code,
            expiresAt: data.expiresAt,
          };
          this.startCountdown();
        }
      } catch (_) {
        // no active code is fine
      }
    },
    async generate() {
      if (!this.selectedAccountId) return;
      this.generating = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.post("/integrations/telegram/link/code", {
          accountId: this.selectedAccountId,
        });
        this.activeCode = {
          code: data.code,
          expiresAt: data.expiresAt,
        };
        this.startCountdown();
        this.showSnackbar(this.$t("settings.profile.telegram.codeGenerated"), "success");
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          this.$t("settings.profile.telegram.errors.generate")
        );
      } finally {
        this.generating = false;
      }
    },
    startCountdown() {
      this.stopCountdown();
      const tick = () => {
        if (!this.activeCode) return;
        const remainingMs = new Date(this.activeCode.expiresAt).getTime() - Date.now();
        if (remainingMs <= 0) {
          this.activeCode = null;
          this.countdownLabel = "";
          this.stopCountdown();
          this.refresh();
          return;
        }
        const totalSec = Math.floor(remainingMs / 1000);
        const min = Math.floor(totalSec / 60);
        const sec = String(totalSec % 60).padStart(2, "0");
        this.countdownLabel = `${min}:${sec}`;
      };
      tick();
      this.countdownTimer = setInterval(tick, 1000);
    },
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    async copyCode() {
      if (!this.activeCode) return;
      try {
        await navigator.clipboard.writeText(this.activeCode.code);
        this.showSnackbar(this.$t("settings.profile.telegram.copied"), "success");
      } catch (_) {
        this.showSnackbar(this.$t("settings.profile.telegram.errors.copy"), "error");
      }
    },
    async unlink() {
      if (!this.linkedToBot) return;
      try {
        await axios.delete(`/integrations/telegram/link/${this.linkedToBot.account_id}`);
        this.showSnackbar(this.$t("settings.profile.telegram.unlinked"), "success");
        await this.refresh();
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          this.$t("settings.profile.telegram.errors.unlink")
        );
      }
    },
  },
};
</script>

<style scoped>
.telegram-link-panel__section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--app-text-strong);
  margin-bottom: 4px;
}
.telegram-link-panel__section-icon {
  color: rgba(var(--v-theme-on-surface), 0.5);
  flex-shrink: 0;
}
.telegram-link-panel__code-card {
  border: 1px dashed rgba(var(--v-theme-on-surface), 0.18);
  border-radius: 6px;
  padding: 12px;
}
.telegram-link-panel__code {
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  padding: 6px 12px;
  border-radius: 4px;
  background: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
  user-select: all;
}
</style>
